import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/lib/useSeo";
import { ArrowLeft, Tag, X, Sparkles, Phone, MessageCircle, Car, Bike, AlertTriangle } from "lucide-react";

function nextDayDate(d) {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function ScooterDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pre-fill dates when arriving from /catalog (Iter 31).
  // Accepts YYYY-MM-DD only — invalid values fall back to the defaults.
  const qsStart = searchParams.get("start_at") || "";
  const qsEnd = searchParams.get("end_at") || "";
  const isValidDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(new Date(s).getTime());

  const [scooter, setScooter] = useState(null);
  const [startDate, setStartDate] = useState(isValidDate(qsStart) ? qsStart : nextDayDate(1));
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState(isValidDate(qsEnd) ? qsEnd : nextDayDate(2));
  const [endTime, setEndTime] = useState("");
  const [pickupSlots, setPickupSlots] = useState([]);
  const [returnSlots, setReturnSlots] = useState([]);
  const [license, setLicense] = useState(user?.license_number || "");
  const [loading, setLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [addonsCatalog, setAddonsCatalog] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    api.get(`/scooters/${id}`).then((r) => setScooter(r.data)).catch(() => setScooter(false));
    api.get("/bookings/addons/catalog").then((r) => setAddonsCatalog(r.data || []));
  }, [id]);

  // Iter 50 — SEO: dynamic per-vehicle title & description. Runs after fetch
  // so the H1 model + price + type end up in Google's SERP. Uses vehicle photo
  // as OG image when available.
  const isEnLang = i18n.language === "en";
  const seoModel = (scooter && typeof scooter === "object" && scooter.model) || "";
  const seoRate = (scooter && typeof scooter === "object" && scooter.daily_rate) || 0;
  const seoType = (scooter && typeof scooter === "object" && scooter.vehicle_type) || "scooter";
  const seoImage = (scooter && typeof scooter === "object" && (scooter.image_url || scooter.photo_url)) || "";
  useSeo({
    title: seoModel
      ? (isEnLang
          ? `Rent ${seoModel} on Elba Island — Rent Lupi (from €${seoRate}/day)`
          : `Noleggia ${seoModel} all'Isola d'Elba — Rent Lupi (da €${seoRate}/giorno)`)
      : (isEnLang
          ? "Vehicle rental — Rent Lupi Elba"
          : "Noleggio veicolo — Rent Lupi Elba"),
    description: seoModel
      ? (isEnLang
          ? `Rent ${seoModel} on Elba Island — ${seoType === "auto" ? "car" : "scooter"} rental with in-house workshop, digital contract and instant online booking. From €${seoRate}/day.`
          : `Noleggia ${seoModel} all'Isola d'Elba · ${seoType === "auto" ? "auto" : "scooter"} a noleggio con officina interna, contratto digitale, prenotazione online · da €${seoRate}/giorno.`)
      : undefined,
    image: seoImage || undefined,
    canonical: `https://rentlupielba.it/scooter/${id}`,
    jsonLd: seoModel
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": seoModel,
          "image": seoImage || undefined,
          "brand": { "@type": "Brand", "name": "Rent Lupi" },
          "category": seoType === "auto" ? "Car rental" : "Scooter rental",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "EUR",
            "price": String(seoRate),
            "availability": "https://schema.org/InStock",
            "url": `https://rentlupielba.it/scooter/${id}`,
            "seller": { "@type": "AutoRental", "@id": "https://rentlupielba.it/#business" },
          },
        }
      : undefined,
  });

  // Load pickup slots whenever startDate changes
  useEffect(() => {
    if (!startDate) return;
    api.get("/bookings/slots", { params: { date: startDate, kind: "pickup" } })
      .then((r) => {
        setPickupSlots(r.data.slots || []);
        setStartTime((cur) => (r.data.slots || []).includes(cur) ? cur : (r.data.slots?.[0] || ""));
      })
      .catch(() => { setPickupSlots([]); setStartTime(""); });
  }, [startDate]);

  // Load return slots whenever endDate changes
  useEffect(() => {
    if (!endDate) return;
    api.get("/bookings/slots", { params: { date: endDate, kind: "return" } })
      .then((r) => {
        setReturnSlots(r.data.slots || []);
        setEndTime((cur) => (r.data.slots || []).includes(cur) ? cur : (r.data.slots?.[0] || ""));
      })
      .catch(() => { setReturnSlots([]); setEndTime(""); });
  }, [endDate]);

  // Compose ISO strings using LOCAL time (Europe/Rome). The user picks a
  // wall-clock time; we send it preserving the offset so the server reads the
  // SAME hour back (the slot validator compares hour/minute components).
  function composeIso(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    const local = new Date(`${dateStr}T${timeStr}:00`);
    if (Number.isNaN(local.getTime())) return null;
    const off = -local.getTimezoneOffset();
    const sign = off >= 0 ? "+" : "-";
    const pad = (n) => String(Math.abs(Math.trunc(n))).padStart(2, "0");
    const offStr = `${sign}${pad(off / 60)}:${pad(off % 60)}`;
    return `${dateStr}T${timeStr}:00${offStr}`;
  }

  const start = composeIso(startDate, startTime);
  const end = composeIso(endDate, endTime);

  const dailyRate = scooter ? (scooter.daily_rate || scooter.hourly_rate || 0) : 0;

  const { days, rentalSubtotal, addonsTotal, subtotal, discount, total, discountSource, durationPct, lastMinutePct } = useMemo(() => {
    const empty = { days: 0, rentalSubtotal: 0, addonsTotal: 0, subtotal: 0, discount: 0, total: 0, discountSource: null, durationPct: 0, lastMinutePct: 0 };
    if (!scooter || !start || !end) return empty;
    const s = new Date(start);
    const e = new Date(end);
    let d = (e - s) / 86400000;
    if (!isFinite(d) || d <= 0) return empty;
    d = Math.max(1, Math.ceil(d));
    const rs = Math.round(d * dailyRate * 100) / 100;
    const at = addons.reduce((sum, a) => sum + (addonsCatalog.find((x) => x.id === a)?.price || 0), 0);
    const sub = Math.round((rs + at) * 100) / 100;

    // Duration discount: 7+ days → 5%, 10+ days → 7.5%. Applied to rental subtotal only.
    // ⚠️ Keep this in sync with /app/backend/core/pricing.py DURATION_TIERS.
    // BUILD_TAG: pricing-v2-5pct-7.5pct (force fresh bundle hash on next deploy)
    const durPct = d >= 10 ? 7.5 : d >= 7 ? 5 : 0;
    const durDisc = Math.round((rs * durPct) / 100 * 100) / 100;

    // Promo discount (if any)
    let promoDisc = 0;
    if (promo) {
      if (promo.discount_type === "percent") {
        promoDisc = Math.round(sub * promo.discount_value) / 100;
      } else if (promo.discount_type === "per_day") {
        promoDisc = Math.min(promo.discount_value * Math.max(1, d), sub);
      } else {
        promoDisc = Math.min(promo.discount_value, sub);
      }
      promoDisc = Math.round(promoDisc * 100) / 100;
    }

    // Iter 46 — Last-minute discount (admin-toggled on the scooter). Mirrors
    // the backend logic in routers/bookings.py so the customer sees the same
    // total as what's charged at checkout. All three discounts are
    // NON-STACKABLE: customer gets the largest.
    let lmDisc = 0;
    let lmPct = 0;
    if (scooter.last_minute_active && scooter.last_minute_discount_pct) {
      const today = new Date().toISOString().slice(0, 10);
      const untilOk = !scooter.last_minute_until || scooter.last_minute_until >= today;
      if (untilOk) {
        lmPct = Number(scooter.last_minute_discount_pct);
        lmDisc = Math.round((rs * lmPct) / 100 * 100) / 100;
      }
    }

    // Customer always gets the LARGEST of the three (they never stack).
    const candidates = [
      { amount: durDisc, source: "duration" },
      { amount: promoDisc, source: "promo" },
      { amount: lmDisc, source: "last_minute" },
    ];
    const winner = candidates.reduce((a, b) => (b.amount > a.amount ? b : a));
    const finalDisc = winner.amount;
    const source = finalDisc > 0 ? winner.source : null;

    return {
      days: d, rentalSubtotal: rs, addonsTotal: Math.round(at * 100) / 100,
      subtotal: sub, discount: finalDisc, durationPct: durPct, lastMinutePct: lmPct,
      total: Math.max(0, Math.round((sub - finalDisc) * 100) / 100),
      discountSource: source,
    };
  }, [start, end, scooter, addons, addonsCatalog, promo, dailyRate]);

  const toggleAddon = (aid) =>
    setAddons((cur) => (cur.includes(aid) ? cur.filter((x) => x !== aid) : [...cur, aid]));

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/promo-codes/validate", { code: promoCode.trim(), subtotal, days });
      setPromo(data);
      toast.success(i18n.language === "en" ? "Promo applied" : "Codice promo applicato");
    } catch (err) {
      setPromo(null);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setValidating(false); }
  };

  const removePromo = () => { setPromo(null); setPromoCode(""); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!license.trim()) { toast.error(t("detail.enter_license")); return; }
    if (!start || !end) { toast.error(isEn ? "Please pick pickup and return slots" : "Seleziona orario di ritiro e riconsegna"); return; }
    if (days <= 0) { toast.error(t("detail.invalid_dates")); return; }
    if (addons.includes("home_delivery") && deliveryAddress.trim().length < 5) {
      toast.error(isEn
        ? "Please enter the delivery address (or hotel name) for home delivery."
        : "Indica l'indirizzo o il nome dell'hotel per la consegna a domicilio.");
      return;
    }
    // Iter 49 — guests can browse the catalog but must register/login to book.
    // We preserve the picked dates in the return URL so after login we come
    // straight back to this scooter with dates pre-filled.
    if (!user || user === false) {
      toast.info(isEn ? "Please sign in to complete your booking" : "Accedi per completare la prenotazione");
      const returnTo = `/scooter/${id}?start_at=${startDate}&end_at=${endDate}`;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        scooter_id: id,
        start_at: start,
        end_at: end,
        license_number: license.trim(),
        addons,
      };
      if (addons.includes("home_delivery")) payload.delivery_address = deliveryAddress.trim();
      if (promo) payload.promo_code = promo.code;
      const { data } = await api.post("/bookings", payload);
      toast.success(t("detail.booking_created"));
      navigate(`/checkout/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (scooter === null) return <div className="text-zinc-400">{t("common.loading")}</div>;
  if (scooter === false) return <div className="text-zinc-400">{t("detail.not_found")}</div>;

  const isEn = i18n.language === "en";
  const statusInfo = {
    disponibile:  null,
    prenotato:    { cls: "border-amber-500/40 bg-amber-500/10 text-amber-200",
                    msg: isEn ? "This vehicle is currently booked. You can still reserve it for available future dates — the system will check overlap automatically."
                              : "Questo veicolo è attualmente prenotato. Puoi comunque riservarlo per date future disponibili — il sistema verifica eventuali sovrapposizioni." },
    noleggiato:   { cls: "border-amber-500/40 bg-amber-500/10 text-amber-200",
                    msg: isEn ? "This vehicle is currently rented. You can still book it for available future dates — the system will check overlap automatically."
                              : "Questo veicolo è attualmente noleggiato. Puoi comunque prenotarlo per date future disponibili — il sistema verifica eventuali sovrapposizioni." },
    sospeso:      { cls: "border-sky-500/40 bg-sky-500/10 text-sky-200",
                    msg: isEn ? "Off-season storage — bookable for the next season only."
                              : "Veicolo in rimessaggio invernale — prenotabile solo per la prossima stagione." },
    manutenzione: { cls: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300",
                    msg: isEn ? "This vehicle is in maintenance and cannot be booked at the moment. Please choose a different one."
                              : "Veicolo in manutenzione: non è prenotabile al momento. Scegli un altro veicolo." },
  };
  const banner = statusInfo[scooter.status];
  const isMaintenance = scooter.status === "manutenzione";

  return (
    <div className="space-y-6">
      <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#FFD700]" data-testid="back-to-catalog">
        <ArrowLeft className="w-4 h-4" /> {t("detail.back_to_catalog")}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-900/30">
          <img src={scooter.photo_url} alt={scooter.model} className="w-full aspect-[4/3] object-cover" />
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{scooter.displacement} cc</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{scooter.model}</h1>
          <p className="text-zinc-400 mt-1">{t("detail.plate", { plate: scooter.plate })}</p>

          <div className="mt-4 inline-flex items-baseline gap-2 border-t-2 border-[#FFD700] pt-3">
            <span className="font-display font-bold text-3xl text-[#FFD700]">€{dailyRate.toFixed(2)}</span>
            <span className="text-zinc-500">{t("detail.per_day")}</span>
          </div>

          {scooter.deposit_amount > 0 && (
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed" data-testid="scooter-deposit-info">
              <span className="text-[#FFD700] font-semibold">{isEn ? "Security deposit" : "Cauzione"}</span>: <span className="text-zinc-100 font-semibold">€{Number(scooter.deposit_amount).toFixed(0)}</span>{" "}
              {isEn
                ? "— paid at pickup, released immediately at hand-back after damage check."
                : "— versata al ritiro, restituita subito alla riconsegna dopo verifica danni."}
            </p>
          )}

          {banner && (
            <div className={`mt-4 rounded-lg border px-4 py-3 text-sm leading-relaxed ${banner.cls}`} data-testid="scooter-status-banner">
              <p>{banner.msg}</p>
              {isMaintenance && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="scooter-maint-cta">
                  <a href="tel:+390565904134"
                     className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2 rounded-md bg-zinc-900 border border-zinc-700 hover:border-[#FFD700] text-sm font-medium text-zinc-100 hover:text-[#FFD700] transition"
                     data-testid="scooter-maint-call">
                    <Phone className="w-4 h-4" /> {isEn ? "Call us" : "Chiamaci"} · 0565 904134
                  </a>
                  <a href="https://wa.me/390565904134"
                     target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2 rounded-md bg-emerald-600/20 border border-emerald-600/40 hover:bg-emerald-600/30 text-sm font-medium text-emerald-300 transition"
                     data-testid="scooter-maint-wa">
                    <MessageCircle className="w-4 h-4" /> {isEn ? "Write on WhatsApp" : "Scrivici su WhatsApp"}
                  </a>
                </div>
              )}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-8 space-y-4 bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
            {/* Age & licence disclosure — required pre-contract (D.Lgs. 206/2005 art.49).
                Prominent yellow banner when the vehicle is a car. */}
            {scooter.vehicle_type === "auto" ? (
              <div className="rounded-xl border-2 border-[#FFD700] bg-[#FFD700]/[0.08] px-4 py-3 flex items-start gap-3" data-testid="age-banner-detail-auto">
                <Car className="w-5 h-5 text-[#FFD700] shrink-0 mt-0.5" />
                <div className="text-sm text-zinc-100 leading-snug flex-1">
                  <div className="font-bold text-[#FFD700] mb-0.5">
                    {isEn ? "21+ — Car rental requirements" : "21+ — Requisiti noleggio auto"}
                  </div>
                  <p className="text-[12.5px] text-zinc-200">
                    {isEn
                      ? "Minimum age 21. Valid category B licence held ≥ 1 year. ID + original licence required at pickup."
                      : "Età minima 21 anni compiuti · patente B da almeno 1 anno · documento e patente in originale al ritiro."}
                  </p>
                  <div className="mt-1.5 pt-1.5 border-t border-[#FFD700]/40 flex items-start gap-1.5 text-[12px]">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-red-200">
                      <span className="font-semibold">{isEn ? "NOT allowed: " : "NON consentito: "}</span>
                      {isEn
                        ? "drivers under 18, learner's permit (\"foglio rosa\") only — only a valid driving licence is accepted."
                        : "minorenni; con solo foglio rosa — è richiesta patente valida."}
                    </p>
                  </div>
                  {/* Iter 61 — Car-only house rules: no smoking, no pets */}
                  <div className="mt-2 pt-1.5 border-t border-[#FFD700]/40" data-testid="car-house-rules">
                    <div className="text-[10px] text-[#FFD700] font-bold uppercase tracking-widest mb-1">
                      {isEn ? "House rules on board" : "Regole a bordo"}
                    </div>
                    <div className="flex flex-wrap gap-2 text-[12px]">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-200 font-semibold">
                        🚭 {isEn ? "No smoking" : "Vietato fumare"}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-200 font-semibold">
                        🐾 {isEn ? "No pets of any kind" : "Vietati animali di qualsiasi genere"}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-1.5 leading-snug">
                      {isEn
                        ? "Non-compliance may incur a professional detailing fee deducted from the deposit."
                        : "In caso di violazione verranno addebitate le spese di pulizia professionale, trattenute dalla cauzione."}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-[#FFD700]/50 bg-[#FFD700]/[0.05] px-3 py-2 text-[11.5px] text-zinc-200 leading-snug" data-testid="age-banner-detail-scooter">
                <div className="flex items-center gap-2">
                  <Bike className="w-3.5 h-3.5 text-[#FFD700] shrink-0" />
                  <span>
                    {isEn
                      ? "18+ · Valid licence (A1/A2/A or B depending on engine cc) required at pickup."
                      : "18+ · Patente valida (A1/A2/A o B in base alla cilindrata) richiesta al ritiro."}
                  </span>
                </div>
                <div className="mt-1 pt-1 border-t border-[#FFD700]/20 flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-red-200 text-[11px]">
                    <span className="font-semibold">{isEn ? "NOT allowed: " : "NON consentito: "}</span>
                    {isEn ? "under 18; learner's permit (\"foglio rosa\") only." : "minorenni; con solo foglio rosa."}
                  </p>
                </div>
              </div>
            )}

            <div className="rounded-md border border-[#FFD700]/30 bg-[#FFD700]/[0.04] px-3 py-2 text-[11px] text-zinc-300 leading-relaxed" data-testid="opening-hours-note">
              <div className="font-semibold text-[#FFD700] uppercase tracking-widest text-[10px] mb-1">
                {isEn ? "Office hours" : "Orari sede"}
              </div>
              <div>{isEn ? "Mon–Fri: pickup 09:00–13:00 · return 15:00–19:00" : "Lun–Ven: ritiro 09:00–13:00 · riconsegna 15:00–19:00"}</div>
              <div>{isEn ? "Sat: pickup by appointment · return 20:00–20:30" : "Sab: ritiro su appuntamento · riconsegna 20:00–20:30"}</div>
              <div>{isEn ? "Sun: no pickup · return 20:00–20:30" : "Dom: niente ritiro · riconsegna 20:00–20:30"}</div>
              <div className="mt-1.5 pt-1.5 border-t border-[#FFD700]/20 text-[#FFD700]/90">
                {isEn ? "Online bookings require min. 48h notice — for last-minute pickups WhatsApp us at " : "Prenotazioni online con almeno 48h di anticipo — per noleggi nelle prossime 48h scrivici su WhatsApp "}
                <a href="https://wa.me/390565904134" target="_blank" rel="noreferrer" className="underline font-semibold" data-testid="opening-hours-whatsapp">+39 0565 904134</a>.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">{isEn ? "Pickup" : "Ritiro"} · {t("detail.start_rental")}</Label>
                <Input type="date" min={nextDayDate(2)} value={startDate} onChange={(e) => setStartDate(e.target.value)}
                       className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50"
                       data-testid="booking-start-date-input" />
                {pickupSlots.length === 0 ? (
                  <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2" data-testid="no-pickup-slots">
                    {isEn ? "Closed for pickup on the selected day. Pick another date." : "Chiusi al ritiro nel giorno selezionato. Scegli un altro giorno."}
                  </div>
                ) : (
                  <select value={startTime} onChange={(e) => setStartTime(e.target.value)}
                          className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                          data-testid="booking-start-time-select">
                    {pickupSlots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{isEn ? "Return" : "Riconsegna"} · {t("detail.end_rental")}</Label>
                <Input type="date" min={startDate || nextDayDate(0)} value={endDate} onChange={(e) => setEndDate(e.target.value)}
                       className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50"
                       data-testid="booking-end-date-input" />
                {returnSlots.length === 0 ? (
                  <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2" data-testid="no-return-slots">
                    {isEn ? "No return slot available for the selected day." : "Nessun orario di riconsegna disponibile per quel giorno."}
                  </div>
                ) : (
                  <select value={endTime} onChange={(e) => setEndTime(e.target.value)}
                          className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                          data-testid="booking-end-time-select">
                    {returnSlots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">{t("auth.license_number")}</Label>
              <Input value={license} onChange={(e) => setLicense(e.target.value)}
                     placeholder={t("detail.license_placeholder")}
                     className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 mt-1.5 uppercase"
                     data-testid="booking-license-input" />
            </div>

            {/* Optional add-ons (home delivery, etc.) */}
            {addonsCatalog.length > 0 && (
              <div className="border border-[#FFD700]/30 rounded-lg p-3 bg-[#FFD700]/5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FFD700] mb-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  {isEn ? "Add-ons" : "Bundle e accessori"}
                </div>
                {addonsCatalog.map((a) => (
                  <label key={a.id} className="flex items-start gap-3 py-1 cursor-pointer">
                    <Checkbox checked={addons.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)}
                              className="mt-0.5 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-black data-[state=checked]:border-[#FFD700]"
                              data-testid={`addon-${a.id}`} />
                    <div className="flex-1">
                      <div className="text-sm">{isEn ? a.label_en : a.label_it}</div>
                    </div>
                    <div className="text-sm font-semibold text-[#FFD700]">+€{a.price.toFixed(2)}</div>
                  </label>
                ))}
                {addons.includes("home_delivery") && (
                  <div className="mt-3 pt-3 border-t border-[#FFD700]/20 space-y-2" data-testid="home-delivery-fields">
                    <Label className="text-zinc-200 text-xs uppercase tracking-widest">
                      {isEn ? "Delivery address or hotel name" : "Indirizzo di consegna o nome hotel"} <span className="text-[#FFD700]">*</span>
                    </Label>
                    <Input
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder={isEn
                        ? "e.g. Hotel La Conchiglia – Marciana Marina, or Via Roma 12"
                        : "es. Hotel La Conchiglia – Marciana Marina, oppure Via Roma 12"}
                      className="bg-zinc-950 border-zinc-800"
                      minLength={5}
                      required
                      data-testid="home-delivery-address-input"
                    />
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 leading-relaxed">
                      {isEn ? (
                        <>
                          🛵 Home deliveries happen at predefined time slots. Please <a
                            href="tel:+390565904134"
                            className="font-semibold text-[#FFD700] underline hover:brightness-110"
                            data-testid="home-delivery-call">call us</a> or <a
                            href="https://wa.me/390565904134"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-emerald-300 underline hover:brightness-110"
                            data-testid="home-delivery-wa">write on WhatsApp</a> to agree on the exact delivery time.
                        </>
                      ) : (
                        <>
                          🛵 Le consegne a domicilio avvengono ad orari predefiniti. Ti invitiamo a <a
                            href="tel:+390565904134"
                            className="font-semibold text-[#FFD700] underline hover:brightness-110"
                            data-testid="home-delivery-call">chiamarci</a> o <a
                            href="https://wa.me/390565904134"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-emerald-300 underline hover:brightness-110"
                            data-testid="home-delivery-wa">scriverci su WhatsApp</a> per concordare l&apos;orario esatto.
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Promo code */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {isEn ? "Promo / Hotel code" : "Codice promo / hotel"}</Label>
              <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
                {isEn
                  ? "Promo codes are not stackable with automatic long-stay discounts (5% from 7 days, 7.5% from 10 days) — the highest discount is applied automatically."
                  : "Il codice promo non è cumulabile con gli sconti automatici per noleggi lunghi (5% da 7 giorni, 7,5% da 10 giorni) — viene applicato lo sconto più vantaggioso."}
              </p>
              {promo ? (
                <div className="mt-1.5 flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2">
                  <div className="text-sm">
                    <span className="font-mono uppercase font-semibold text-emerald-400">{promo.code}</span>
                    {promo.hotel_name && <span className="text-zinc-400"> · {promo.hotel_name}</span>}
                    <div className="text-xs text-zinc-500">
                      −{promo.discount_type === "percent"
                          ? `${promo.discount_value}%`
                          : promo.discount_type === "per_day"
                            ? `€${promo.discount_value.toFixed(2)}/${isEn ? "day" : "giorno"}`
                            : `€${promo.discount_value.toFixed(2)}`}
                    </div>
                  </div>
                  <button type="button" onClick={removePromo} className="text-zinc-400 hover:text-zinc-100" data-testid="remove-promo">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-1.5 flex gap-2">
                  <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                         placeholder={isEn ? "Enter code" : "Inserisci codice"}
                         className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 uppercase font-mono"
                         data-testid="promo-input" />
                  <Button type="button" onClick={applyPromo} disabled={validating || !promoCode.trim()}
                          variant="outline" className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800"
                          data-testid="apply-promo-button">
                    {validating ? "…" : (isEn ? "Apply" : "Applica")}
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t border-zinc-800 pt-4 space-y-1 text-sm">
              <Row label={`${days} ${days === 1 ? t("detail.day_one") : t("detail.day_other")} × €${dailyRate.toFixed(2)}`} value={`€${rentalSubtotal.toFixed(2)}`} />
              {addonsTotal > 0 && <Row label={t("detail.addons_title")} value={`+€${addonsTotal.toFixed(2)}`} />}
              {discount > 0 && (
                <Row
                  label={discountSource === "duration"
                    ? (isEn ? `Long-stay discount (−${durationPct}%)` : `Sconto durata (−${durationPct}%)`)
                    : discountSource === "last_minute"
                      ? (isEn ? `🔥 Last-minute discount (−${lastMinutePct}%)` : `🔥 Sconto Last Minute (−${lastMinutePct}%)`)
                      : t("detail.discount")}
                  value={`−€${discount.toFixed(2)}`}
                  accent
                />
              )}
              {/* Hint shown BEFORE reaching the discount tier */}
              {durationPct === 0 && days > 0 && days < 7 && (
                <div className="text-[11px] text-zinc-500 mt-2 leading-snug" data-testid="duration-hint">
                  {isEn
                    ? `💡 ${7 - days} more day${7 - days === 1 ? "" : "s"} for an automatic 5% discount.`
                    : `💡 Ancora ${7 - days} giorn${7 - days === 1 ? "o" : "i"} per uno sconto automatico del 5%.`}
                </div>
              )}
              {durationPct === 5 && (
                <div className="text-[11px] text-[#FFD700] mt-2 leading-snug" data-testid="duration-tip-5">
                  {isEn
                    ? `🎉 Auto-discount applied. ${10 - days} more day${10 - days === 1 ? "" : "s"} for 7.5% off.`
                    : `🎉 Sconto automatico attivo. Ancora ${10 - days} giorn${10 - days === 1 ? "o" : "i"} per arrivare al 7,5%.`}
                </div>
              )}
              {durationPct === 7.5 && (
                <div className="text-[11px] text-[#FFD700] mt-2 leading-snug" data-testid="duration-tip-7-5">
                  {isEn
                    ? "🎉 Maximum long-stay discount applied (−7.5%)."
                    : "🎉 Sconto durata massimo applicato (−7,5%)."}
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <div className="text-xs uppercase tracking-widest text-zinc-500">{t("detail.total")}</div>
                <div className="font-display font-bold text-3xl text-[#FFD700]" data-testid="booking-total">€{total.toFixed(2)}</div>
              </div>
            </div>

            <Button type="submit" disabled={loading || days <= 0 || isMaintenance || !startTime || !endTime}
                    className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="booking-submit-button">
              {isMaintenance
                ? (isEn ? "Not bookable" : "Non prenotabile")
                : (loading
                    ? t("detail.creating")
                    : (!user || user === false)
                      ? (isEn ? "Sign in to book" : "Accedi per prenotare")
                      : t("detail.proceed_checkout"))}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-medium ${accent ? "text-emerald-400" : "text-zinc-100"}`}>{value}</span>
    </div>
  );
}
